/**
 * The map store: every published map kept once on the phone, by edition (roadmap, "Next (after Head Trip): a map
 * store"). Maps used to live in the one persisted query blob (localStorage on web, Capacitor Preferences on iOS,
 * 4 MB cap): two or three fit, each of the Bowl's 18 shows kept its own copy of the one venue map, and every persist
 * rewrote ~2.4 MB on the main thread.
 *
 * Two IndexedDB stores, the same database technology the image store already relies on in the iOS WebView
 * (lib/images/imageCache.js):
 *   maps    editionId -> { editionId, version, payload, at }   one copy per published map
 *   events  eventId   -> { eventId, editionId, version, pinned, at }
 *           editionId null = "this event has no map" (known offline, so Find does not wait for one)
 *
 * A festival's offline set PINS its events; pinned maps are never pruned. Anything else (an event page that showed a
 * map) is kept oldest-used-first up to MAX_EDITIONS. An in-memory mirror of the events index answers "is it on the
 * phone?" synchronously for the download pill.
 *
 * The backend is injectable (createMapStore) so the logic is testable without IndexedDB.
 */

export const MAX_EDITIONS = 12;

/** An IndexedDB backend: get/put/delete/all over two object stores. Resolves null when IndexedDB is unavailable. */
/** WebKit can leave an IndexedDB request unanswered (a dead connection after a long background): never wait forever. */
const IDB_TIMEOUT_MS = 4000;
const withTimeout = (p, fallback) =>
  Promise.race([p, new Promise((resolve) => setTimeout(() => resolve(fallback), IDB_TIMEOUT_MS))]);

export function indexedDbBackend(dbName = 'prii-maps') {
  let dbPromise = null;
  const open = () => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve) => {
      try {
        if (typeof indexedDB === 'undefined') {
          resolve(null);
          return;
        }
        const req = indexedDB.open(dbName, 1);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains('maps'))
            db.createObjectStore('maps', { keyPath: 'editionId' });
          if (!db.objectStoreNames.contains('events'))
            db.createObjectStore('events', { keyPath: 'eventId' });
        };
        req.onsuccess = () => {
          const db = req.result;
          // WebKit closes long-idle connections; a memoised dead one would fail every read until relaunch.
          db.onclose = () => {
            dbPromise = null;
          };
          db.onversionchange = () => {
            db.close();
            dbPromise = null;
          };
          resolve(db);
        };
        req.onerror = () => {
          dbPromise = null;
          resolve(null);
        };
        req.onblocked = () => {
          dbPromise = null;
          resolve(null);
        };
      } catch {
        dbPromise = null;
        resolve(null);
      }
    });
    const p = dbPromise;
    return withTimeout(p, null).then((db) => {
      if (!db && dbPromise === p) dbPromise = null;
      return db;
    });
  };
  const run = async (store, mode, fn) => {
    const db = await open();
    if (!db) return undefined;
    return withTimeout(
      new Promise((resolve, reject) => {
        let tx;
        try {
          tx = db.transaction(store, mode);
        } catch (err) {
          dbPromise = null;
          reject(err);
          return;
        }
        const req = fn(tx.objectStore(store));
        tx.oncomplete = () => resolve(req?.result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      }),
      undefined,
    );
  };
  return {
    get: (store, key) => run(store, 'readonly', (s) => s.get(key)),
    put: (store, value) => run(store, 'readwrite', (s) => s.put(value)),
    delete: (store, key) => run(store, 'readwrite', (s) => s.delete(key)),
    all: (store) => run(store, 'readonly', (s) => s.getAll()),
    /** Just the keys: a store's names without reading (and copying, on the main thread) every map in it. */
    keys: (store) => run(store, 'readonly', (s) => s.getAllKeys()),
  };
}

/** A Map-backed backend for tests. */
export function memoryBackend() {
  const stores = { maps: new Map(), events: new Map() };
  const keyOf = (store, v) => (store === 'maps' ? v.editionId : v.eventId);
  return {
    stores,
    get: async (store, key) => stores[store].get(key),
    put: async (store, value) => {
      stores[store].set(keyOf(store, value), structuredClone(value));
    },
    delete: async (store, key) => {
      stores[store].delete(key);
    },
    all: async (store) => [...stores[store].values()],
    keys: async (store) => [...stores[store].keys()],
  };
}

/**
 * The payload as it can be stored. A freshly downloaded map is handed to the 3D build before it is written here, and the
 * build hangs its working state on the features (functions like _yAt, _deck): IndexedDB refused the whole map ("The
 * object can not be cloned", 2026-10-21), so the store kept the old version and every open downloaded and built the map
 * again. Underscore keys are the build's own (serializeFeatures drops them too); functions never belong in a map.
 */
export function storable(payload) {
  return JSON.parse(
    JSON.stringify(payload, (k, v) =>
      typeof v === 'function' || (typeof k === 'string' && k.charCodeAt(0) === 95) ? undefined : v,
    ),
  );
}

function editionIdOf(payload) {
  return payload?.properties?.edition_id ?? null;
}

export function createMapStore(
  backend,
  { now = () => Date.now(), maxEditions = MAX_EDITIONS } = {},
) {
  const index = new Map(); // eventId -> events row
  // The last couple of editions as live objects, shared by every show that uses them (Sam, 2026-10-22: a second show at
  // the Bowl should open like a revisit). The 3D map keeps its scene for the SAME scene3d object, and a read from the
  // backend is a fresh copy every time - so without this each show at a venue rebuilt the whole scene.
  const live = new Map(); // editionId -> { version, payload }
  const LIVE_EDITIONS = 2;
  const keepLive = (editionId, version, payload) => {
    live.delete(editionId);
    live.set(editionId, { version, payload });
    while (live.size > LIVE_EDITIONS) live.delete(live.keys().next().value);
  };
  const listeners = new Set();
  let loaded = null;
  const notify = () => {
    for (const cb of listeners) cb();
  };
  // Every change goes through one queue: a prune between a write's map put and its index row read the new map as
  // orphaned and deleted it (two events written at once - an import and an event page).
  let queue = Promise.resolve();
  const serial = (fn) => {
    const run = queue.then(fn, fn);
    queue = run.catch(() => {});
    return run;
  };

  const load = () => {
    if (!loaded) {
      loaded = (async () => {
        try {
          for (const row of (await backend.all('events')) ?? []) index.set(row.eventId, row);
        } catch (err) {
          console.warn('[map-store] index read failed:', err);
        }
        notify();
      })();
    }
    return loaded;
  };

  async function prune() {
    const pinned = new Set();
    const used = new Map(); // editionId -> latest use
    for (const row of index.values()) {
      if (!row.editionId) continue;
      if (row.pinned) pinned.add(row.editionId);
      used.set(row.editionId, Math.max(used.get(row.editionId) ?? 0, row.at ?? 0));
    }
    // The stored maps' NAMES only (2026-10-30): this read every stored map in full - several MB each, copied on the main
    // thread - right after each download, and taps waited for it ("I can't tap away until it's done downloading").
    const ids = (await backend.keys('maps')) ?? [];
    // Blobs no event points at any more (a republish moved every event to a new edition).
    const orphans = ids.filter((id) => !used.has(id));
    const loose = ids
      .filter((id) => used.has(id) && !pinned.has(id))
      .sort((a, b) => (used.get(a) ?? 0) - (used.get(b) ?? 0));
    const keepLoose = Math.max(2, maxEditions - pinned.size); // a few slots for viewed maps even when many are pinned
    const evict = [...orphans, ...loose.slice(0, Math.max(0, loose.length - keepLoose))];
    for (const editionId of evict) {
      await backend.delete('maps', editionId);
      for (const row of [...index.values()]) {
        if (row.editionId === editionId) {
          index.delete(row.eventId);
          await backend.delete('events', row.eventId);
        }
      }
    }
    if (evict.length) notify();
  }

  return {
    ready: load,

    subscribe(cb) {
      listeners.add(cb);
      void load();
      return () => listeners.delete(cb);
    },

    /** Synchronous: the event's map (or its known absence) is on the phone. False until the index has loaded. */
    has(eventId) {
      return index.has(eventId);
    },

    /** The stored version stamp (get_festival_map_version) for the event's map, or null. */
    version(eventId) {
      return index.get(eventId)?.version ?? null;
    },

    /** The stored payload: a FeatureCollection, null for "no map", or undefined when nothing is stored. */
    async read(eventId) {
      await load();
      const row = index.get(eventId);
      if (!row) return undefined;
      if (!row.editionId) return null;
      const hot = live.get(row.editionId);
      if (hot && hot.version === row.version) return withEvent(hot.payload, eventId);
      const map = await backend.get('maps', row.editionId);
      if (!map) {
        // the row outlived its map: forget it everywhere, so the phone stops saying it is saved
        index.delete(eventId);
        await serial(() => backend.delete('events', eventId)).catch(() => {});
        notify();
        return undefined;
      }
      if (now() - (row.at ?? 0) > 60 * 60 * 1000) {
        row.at = now();
        void backend.put('events', row).catch(() => {});
      }
      keepLive(row.editionId, row.version, map.payload);
      return withEvent(map.payload, eventId);
    },

    /** A show not seen yet whose map (by its version, get_festival_map_version) is already on the phone: point it at that
     *  copy instead of downloading the same venue map again. True when it did. */
    adopt(eventId, version) {
      return serial(() => this._adopt(eventId, version));
    },
    async _adopt(eventId, version) {
      await load();
      const editionId = typeof version === 'string' ? version.split(':')[0] : null;
      if (!editionId) return false;
      const map = live.get(editionId) ?? (await backend.get('maps', editionId));
      if (!map || map.version !== version) return false;
      const row = {
        eventId,
        editionId,
        version,
        pinned: index.get(eventId)?.pinned ?? false,
        at: now(),
      };
      await backend.put('events', row);
      index.set(eventId, row);
      notify();
      return true;
    },

    /** Store what the server answered for this event. Shared editions (a venue map) are written once. */
    write(eventId, payload, opts) {
      return serial(() => this._write(eventId, payload, opts));
    },
    async _write(eventId, payload, { version = null, pin } = {}) {
      await load();
      const prev = index.get(eventId);
      const editionId = payload ? editionIdOf(payload) : null;
      if (payload && !editionId) return; // nothing to key it by (a dev draft); keep it out of the store
      const row = {
        eventId,
        editionId,
        version: version ?? (prev?.editionId === editionId ? (prev?.version ?? null) : null),
        pinned: pin ?? prev?.pinned ?? false,
        at: now(),
      };
      if (payload) {
        keepLive(editionId, row.version, payload); // the downloaded object itself: the next show at the venue gets it
        // Already stored at this version: known from the index rows and the store's keys, without reading the map back
        const have = ((await backend.keys('maps')) ?? []).includes(editionId);
        const same =
          have &&
          row.version != null &&
          [...index.values()].some((r) => r.editionId === editionId && r.version === row.version);
        if (!same) {
          await backend.put('maps', {
            editionId,
            version: row.version,
            payload: storable(payload),
            at: now(),
          });
        }
      }
      await backend.put('events', row);
      index.set(eventId, row);
      notify();
      await prune();
    },

    setVersion(eventId, version) {
      return serial(() => this._setVersion(eventId, version));
    },
    async _setVersion(eventId, version) {
      await load();
      const row = index.get(eventId);
      if (!row) return;
      row.version = version;
      await backend.put('events', row);
    },

    pin(eventId, pinned = true) {
      return serial(() => this._pin(eventId, pinned));
    },
    async _pin(eventId, pinned) {
      await load();
      const row = index.get(eventId);
      if (!row || row.pinned === pinned) return;
      row.pinned = pinned;
      await backend.put('events', row);
    },

    /** Forget an event (its festival is over); its map goes once no other event uses it. */
    remove(eventId) {
      return serial(() => this._remove(eventId));
    },
    async _remove(eventId) {
      await load();
      if (!index.has(eventId)) return;
      index.delete(eventId);
      await backend.delete('events', eventId);
      notify();
      await prune();
    },
  };
}

/** A venue map is shared by every show at the venue: the copy carries the first show's id. Same features and terrain
 *  objects underneath, so a scene built for one show is kept for the next. */
function withEvent(p, eventId) {
  return p?.properties ? { ...p, properties: { ...p.properties, event_id: eventId } } : p;
}

/** The app's store. */
export const mapStore = createMapStore(indexedDbBackend());
